import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { getSupportSmsBody } from 'common/utility/helpers';
import { UTILITY_ACTIVATION_STATUS_CANCEL_REASON_CHANGED_ADDRESS } from 'common/utility/constants';

import {
  SCREEN_ID_UTILITY_SETUP_NEW_CUSTOMER_DEALS,
  SCREEN_ID_UTILITY_SETUP_PLACE_ORDER,
  SCREEN_ID_UTILITY_SETUP_PROVIDER_PLAN_CONFIRMATION,
} from 'client/constants/screenIds';

import { SR_API_ACTION_UTILITY_ACTIVATION_UPDATE } from 'api/utilityActionTypes';

import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import NavigatorActions from 'client/actions/Navigator';

import SrBox from 'client/components/SrBox';
import SrUtilitySetupChangeAddressModal from 'client/components/SrUtilitySetup/SrUtilitySetupChangeAddressModal/SrUtilitySetupChangeAddressModal';

import './SrUtilitySetupPage.less';
import { formatPhoneNumber } from 'common/formatters';

const SrUtilitySetupPage = ({
  addressDisplay,
  utilityActivationId,
  utilityPartner,
  logoUrl,
  utilityProviderTheme,
  rightActionComponent,
  backButtonText,
  backButtonUrl,
  children,
}) => {
  const [changeAddressModalArgs, setChangeAddressModalArgs] = useState(null);

  const dispatch = useDispatch();

  const allowAddressChange = !!utilityPartner?.experienceConfiguration?.allowAddressChange;
  const shouldShowCustomerSupportFields =
    !!utilityPartner?.customerSupportPhone || !!utilityPartner?.customerSupportEmail;
  const supportSmsQueryString = !addressDisplay
    ? ''
    : `?&body=${getSupportSmsBody({
        addressStreetDisplay: addressDisplay,
      })}`;

  const { screenId } = useBranch({ screenId: ['analytics', 'screenId'] });

  const pageRequiresHelveticaFont = [
    SCREEN_ID_UTILITY_SETUP_PLACE_ORDER,
    SCREEN_ID_UTILITY_SETUP_PROVIDER_PLAN_CONFIRMATION,
    SCREEN_ID_UTILITY_SETUP_NEW_CUSTOMER_DEALS,
  ].includes(screenId);

  const _onClickBackButton = () => {
    dispatch(NavigatorActions.setShouldRestoreScrollPosition);

    dispatch(NavigatorActions.replace, {
      to: backButtonUrl,
    });
  };

  const _onAddressChange = () => {
    if (!utilityActivationId || !utilityPartner) {
      return;
    }
    setChangeAddressModalArgs({
      addressDisplay,
      utilityActivationId,
      utilityPartner,
      onBack: () => {
        setChangeAddressModalArgs(null);
      },
      onSubmit: () => {
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
          payload: {
            id: utilityActivationId,
            updates: {
              archivedAt: moment().toISOString(),
              cancelReason: UTILITY_ACTIVATION_STATUS_CANCEL_REASON_CHANGED_ADDRESS,
            },
          },
        }).then(() => {
          dispatch(NavigatorActions.replace, {
            to: `/utility-setup/${utilityPartner.slug}`,
          });
        });
      },
    });
  };

  return (
    <>
      <div>
        <div styleName="header">
          {utilityProviderTheme && (
            <div
              styleName="securePortalBox"
              style={{ backgroundColor: utilityProviderTheme?.primaryColor }}
            >
              <span styleName="securePortalText">🔒 Secure payment portal</span>
            </div>
          )}

          <div
            styleName={classnames('headerBox', { centerLogo: !addressDisplay && !backButtonUrl })}
          >
            {logoUrl && <SrBox styleName="logoBox" backgroundImage={`url("${logoUrl}")`} />}
            {addressDisplay && !rightActionComponent && (
              <div styleName="addressContainer">
                <div styleName="addressText">📍 {addressDisplay}</div>
                {allowAddressChange && (
                  <div onClick={_onAddressChange} styleName="addressChangeText">
                    Change Address
                  </div>
                )}
              </div>
            )}
            {rightActionComponent && <div styleName="rightActionBox">{rightActionComponent}</div>}
          </div>
          {backButtonUrl && (
            <div styleName="backButtonBox" onClick={_onClickBackButton}>
              <div styleName="arrowIcon" />
              <span>{backButtonText}</span>
            </div>
          )}
        </div>
        <div styleName="content">{children}</div>
        <div styleName="footer">
          <div styleName="chatBox">
            <span
              styleName={classnames('customerSupportTitle', {
                pageRequiresHelveticaFont: pageRequiresHelveticaFont,
              })}
            >
              {pageRequiresHelveticaFont
                ? 'Have questions about utilities?'
                : '⚡️ Have questions about utilities?'}
            </span>
            <span
              styleName={classnames('customerSupportText', {
                pageRequiresHelveticaFont: pageRequiresHelveticaFont,
              })}
            >
              Get human support (9am–5pm CST):
            </span>
            <a
              href={`sms:+17373771570${supportSmsQueryString}`}
              styleName={classnames('chatText', {
                pageRequiresHelveticaFont: pageRequiresHelveticaFont,
              })}
            >
              Please text (737) 377-1570
            </a>
          </div>
          {shouldShowCustomerSupportFields && (
            <div styleName="chatBox">
              <span
                styleName={classnames('customerSupportTitle', {
                  pageRequiresHelveticaFont: pageRequiresHelveticaFont,
                })}
              >
                {pageRequiresHelveticaFont
                  ? 'Have questions about your lease?'
                  : '🏡 Have questions about your lease?'}
              </span>
              <span
                styleName={classnames('customerSupportText', {
                  pageRequiresHelveticaFont: pageRequiresHelveticaFont,
                })}
              >
                Contact your Property Manager:
              </span>
              {utilityPartner?.customerSupportPhone && (
                <a
                  href={`sms:${utilityPartner.customerSupportPhone}`}
                  styleName={classnames('chatText', {
                    pageRequiresHelveticaFont: pageRequiresHelveticaFont,
                  })}
                >
                  {formatPhoneNumber(utilityPartner.customerSupportPhone)}
                </a>
              )}
              {utilityPartner?.customerSupportEmail && (
                <a
                  href={`mailto:${utilityPartner.customerSupportEmail}`}
                  styleName={classnames('chatText', {
                    pageRequiresHelveticaFont: pageRequiresHelveticaFont,
                  })}
                >
                  {utilityPartner.customerSupportEmail}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      {changeAddressModalArgs && <SrUtilitySetupChangeAddressModal {...changeAddressModalArgs} />}
    </>
  );
};

SrUtilitySetupPage.propTypes = {
  addressDisplay: PropTypes.string,
  utilityPartner: PropTypes.object,
  utilityProviderTheme: PropTypes.object,
  logoUrl: PropTypes.string,
  rightActionComponent: PropTypes.node,
  backButtonText: PropTypes.string,
  backButtonUrl: PropTypes.string,
  children: PropTypes.node,
};

SrUtilitySetupPage.displayName = 'SrUtilitySetupPage';

export default SrUtilitySetupPage;
